// src/store/api/servicesApi.js
// Uses your shared axios instance that already handles baseURL (/api/v1)
// We still pass { withCredentials: true } explicitly on each call.

import request from '../utils/request';

const WITH_CREDS = { withCredentials: true };

/**
 * Small local fallback in case /services/catalog is down.
 * Backend should remain source of truth.
 */
const FALLBACK_CATALOG = [
  {
    key: 'early_checkin',
    name: 'Early Check-In',
    category: 'Arrival',
    description: 'Let guests check in earlier than the standard time.',
    pricingModel: 'flat',
    unitLabel: 'per request',
    icon: 'clock',
    defaults: { enabled: false, basePrice: 35, minPrice: 0, maxPrice: 200, multiplier: 1.0 },
  },
  {
    key: 'late_checkout',
    name: 'Late Check-Out',
    category: 'Departure',
    description: 'Extend stay beyond the normal checkout time.',
    pricingModel: 'flat',
    unitLabel: 'per request',
    icon: 'clock-rotate',
    defaults: { enabled: false, basePrice: 35, minPrice: 0, maxPrice: 200, multiplier: 1.0 },
  },
  {
    key: 'mid_stay_cleaning',
    name: 'Mid-Stay Cleaning',
    category: 'Housekeeping',
    description: 'A full refresh during the stay.',
    pricingModel: 'flat',
    unitLabel: 'per cleaning',
    icon: 'broom',
    defaults: { enabled: false, basePrice: 65, minPrice: 25, maxPrice: 500, multiplier: 1.0 },
  },
  {
    key: 'private_chef',
    name: 'Private Chef',
    category: 'Experiences',
    description: 'Book a private chef for an in-home dining experience.',
    pricingModel: 'api',
    unitLabel: 'dynamic',
    icon: 'chef-hat',
    defaults: { enabled: false, basePrice: null, minPrice: 0, maxPrice: 99999, multiplier: 1.0 },
  },
  {
    key: 'airport_ride',
    name: 'Airport Ride',
    category: 'Transport',
    description: 'On-demand rides to/from the airport (Uber integration).',
    pricingModel: 'api',
    unitLabel: 'dynamic',
    icon: 'car',
    defaults: { enabled: false, basePrice: null, minPrice: 0, maxPrice: 99999, multiplier: 1.2 },
  },
];

function normalizeCatalog(rawList) {
  if (!Array.isArray(rawList)) return FALLBACK_CATALOG;
  const seen = new Set();
  return rawList
    .filter((s) => s && typeof s === 'object' && s.key && !seen.has(s.key) && (seen.add(s.key) || true))
    .map((s) => ({
      key: s.key,
      name: s.name ?? s.key,
      category: s.category ?? 'General',
      description: s.description ?? '',
      pricingModel: s.pricingModel ?? 'flat',
      unitLabel: s.unitLabel ?? 'per request',
      icon: s.icon ?? 'sparkles',
      defaults: {
        enabled: s.defaults?.enabled ?? false,
        basePrice: s.defaults?.basePrice ?? null,
        minPrice: s.defaults?.minPrice ?? 0,
        maxPrice: s.defaults?.maxPrice ?? 99999,
        multiplier: s.defaults?.multiplier ?? 1.0,
      },
      // optional UI fields from backend
      imageUrl: s.imageUrl || '',
      bannerUrl: s.bannerUrl || '',
      providerType: s.providerType,
      pricingMode: s.pricingMode,
      kind: s.kind,
    }));
}

/** GET /api/v1/services/catalog */
export async function fetchServicesCatalog() {
  try {
    const { data } = await request.get('/services/catalog', WITH_CREDS);
    // backend returns { success, services: [...] }
    return normalizeCatalog(data?.services || data);
  } catch {
    return FALLBACK_CATALOG;
  }
}

/** GET /api/v1/services/host/:hostId/all (catalog + host map) */
export async function fetchCatalogAndHost(hostId) {
  const { data } = await request.get(`/services/host/${hostId}/all`, WITH_CREDS);
  // { success, catalog: [...], servicesMap: {...} }
  return data;
}

/** GET /api/v1/services/host/:hostId (host services map only) */
export async function fetchHostServices(hostId) {
  const { data } = await request.get(`/services/host/${hostId}`, WITH_CREDS);
  // backend returns { success, servicesMap }
  return data?.servicesMap || {};
}

/** PATCH /api/v1/services/host/:hostId/:serviceKey */
export async function updateHostServiceAPI(hostId, serviceKey, patch) {
  const { data } = await request.patch(
    `/services/host/${hostId}/${serviceKey}`,
    patch || {},
    WITH_CREDS
  );
  // { success, service }
  return data;
}

/** PUT /api/v1/services/host/:hostId/bulk  body: { updates: [...] } */
export async function saveHostServicesAPI(hostId, updates) {
  const { data } = await request.put(
    `/services/host/${hostId}/bulk`,
    { updates: updates || [] },
    WITH_CREDS
  );
  // { success, servicesMap }
  return data;
}

/** OPTIONAL guest-facing endpoints */
export async function fetchPropertyServices(propertyId) {
  const { data } = await request.get(`/services/properties/${propertyId}/services`, WITH_CREDS);
  return data?.services || {};
}
export async function createServiceQuote(serviceKey, params = {}) {
  const { data } = await request.post('/services/quotes', { serviceKey, params }, WITH_CREDS);
  return data; // { price, currency, breakdown? }
}

/** Utilities */
export function setServicesBaseURL(url) {
  if (request?.defaults) request.defaults.baseURL = url;
}
export function setServicesAuthToken(token) {
  if (request?.defaults?.headers?.common) {
    if (token) request.defaults.headers.common.Authorization = `Bearer ${token}`;
    else delete request.defaults.headers.common.Authorization;
  }
}
export { request as servicesAxios };
